import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Send, Trash2, Sparkles, User, Paperclip, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SUGGESTIONS = [
  "What's our average deal value this month vs last month?",
  "Since we changed the price from £3500 to £3950, has the conversion rate dropped?",
  "How many leads did we get this month and how does it compare to last 3 months?",
  "What's our win rate and how has it changed over time?",
  "Give me a weekly report summary I can send to my business partner",
];

export default function InsightsTab() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `session-${Date.now()}`);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const [attachment, setAttachment] = useState(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => { scrollToBottom(); }, [messages]);

  const loadHistory = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/insights/history?sessionId=${sessionId}`);
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages || []);
      }
    } catch (e) {
      console.error('Failed to load history:', e); // eslint-disable-line no-console
    }
  }, [sessionId]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const sendMessage = async (text) => {
    if ((!text.trim() && !attachment) || loading) return;
    
    let messageText = text;
    let attachmentData = null;
    
    if (attachment) {
      attachmentData = { name: attachment.name, content: attachment.content };
      messageText = text.trim() ? `${text}\n\n[Attached file: ${attachment.name}]` : `Please analyse this file: ${attachment.name}`;
    }
    
    const userMsg = { role: 'user', content: messageText };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setAttachment(null);
    setLoading(true);

    try {
      const payload = { message: messageText, sessionId };
      if (attachmentData) {
        payload.attachmentName = attachmentData.name;
        payload.attachmentContent = attachmentData.content;
      }
      const res = await fetch(`${API}/api/insights/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
      }
    } catch (e) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Failed to get response. Please try again.' }]);
    }
    setLoading(false);
    inputRef.current?.focus();
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setAttachment({ name: file.name, content: ev.target.result });
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const clearChat = async () => {
    try {
      await fetch(`${API}/api/insights/clear?sessionId=${sessionId}`, { method: 'DELETE' });
      setMessages([]);
    } catch (e) {
      console.error('Failed to clear:', e); // eslint-disable-line no-console
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  return (
    <div className="space-y-4" data-testid="insights-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-stone-800">Insights</h2>
          <p className="text-sm text-stone-500">Ask questions about your business data — powered by AI</p>
        </div>
        {messages.length > 0 && (
          <Button onClick={clearChat} variant="outline" size="sm" className="border-stone-200 text-stone-500" data-testid="clear-chat">
            <Trash2 className="w-4 h-4 mr-1" /> Clear
          </Button>
        )}
      </div>

      {/* Chat Area */}
      <Card className="bg-white border-stone-100 shadow-sm overflow-hidden">
        <CardContent className="p-0">
          <div className="h-[500px] overflow-y-auto p-5 space-y-4" data-testid="chat-messages">
            {messages.length === 0 && !loading && (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <div className="p-3 rounded-full bg-purple-50 mb-4">
                  <Sparkles className="w-8 h-8 text-purple-500" />
                </div>
                <h3 className="text-lg font-medium text-stone-800 mb-2">Ask me anything about your data</h3>
                <p className="text-sm text-stone-500 mb-6 max-w-md">
                  I have access to all your Brevo CRM deals, pipeline stages, revenue, and conversion data. Try one of these:
                </p>
                <div className="flex flex-wrap gap-2 max-w-lg justify-center">
                  {SUGGESTIONS.map((s, i) => (
                    <button
                      key={s}
                      onClick={() => sendMessage(s)}
                      className="text-xs text-left px-3 py-2 rounded-lg bg-stone-50 border border-stone-200 text-stone-600 hover:bg-stone-100 hover:border-stone-300 transition-colors"
                      data-testid={`suggestion-${i}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg, i) => (
              <div key={`${msg.role}-${msg.timestamp || i}`} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {msg.role === 'assistant' && (
                  <div className="w-7 h-7 rounded-full bg-purple-100 flex items-center justify-center shrink-0 mt-1">
                    <Sparkles className="w-4 h-4 text-purple-600" />
                  </div>
                )}
                <div className={`max-w-[80%] rounded-xl px-4 py-3 ${
                  msg.role === 'user'
                    ? 'bg-stone-900 text-white'
                    : 'bg-stone-50 border border-stone-100 text-stone-800'
                }`}>
                  <div className={`text-sm whitespace-pre-wrap leading-relaxed ${msg.role === 'assistant' ? 'prose prose-sm prose-stone max-w-none' : ''}`}>
                    {msg.content}
                  </div>
                </div>
                {msg.role === 'user' && (
                  <div className="w-7 h-7 rounded-full bg-stone-200 flex items-center justify-center shrink-0 mt-1">
                    <User className="w-4 h-4 text-stone-600" />
                  </div>
                )}
              </div>
            ))}

            {loading && (
              <div className="flex gap-3">
                <div className="w-7 h-7 rounded-full bg-purple-100 flex items-center justify-center shrink-0">
                  <Sparkles className="w-4 h-4 text-purple-600 animate-pulse" />
                </div>
                <div className="bg-stone-50 border border-stone-100 rounded-xl px-4 py-3">
                  <div className="flex items-center gap-2 text-sm text-stone-500">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 bg-stone-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <div className="w-2 h-2 bg-stone-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <div className="w-2 h-2 bg-stone-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                    Analysing your data...
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="border-t border-stone-100 p-4 bg-stone-50/50">
            {attachment && (
              <div className="flex items-center gap-2 mb-2 px-2 py-1.5 bg-stone-100 rounded-lg text-xs text-stone-600 w-fit">
                <Paperclip className="w-3 h-3" />
                <span className="truncate max-w-[200px]">{attachment.name}</span>
                <button onClick={() => setAttachment(null)} className="hover:text-stone-900">
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}
            <form onSubmit={handleSubmit} className="flex gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.txt,.json,.xlsx,.pdf"
                onChange={handleFileSelect}
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => fileInputRef.current?.click()}
                disabled={loading}
                className="border-stone-200 shrink-0"
                data-testid="insights-attach"
              >
                <Paperclip className="w-4 h-4" />
              </Button>
              <Input
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                placeholder={attachment ? "Add a message about this file..." : "Ask about your business data..."}
                disabled={loading}
                className="flex-1 bg-white"
                data-testid="insights-input"
              />
              <Button
                type="submit"
                disabled={loading || (!input.trim() && !attachment)}
                className="bg-stone-900 text-white hover:bg-stone-800"
                data-testid="insights-send"
              >
                <Send className="w-4 h-4" />
              </Button>
            </form>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
